import './CustomSegments.scss';
import * as uuid from 'uuid';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { isSchemaNameOrPrettyNameCaseInsensitive } from '@blisspointmedia/bpm-types/dist/Columns';
import { isBillingSegmentName } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Dropdown, MenuItem } from '@Components/Dropdown';
import { FullPageSpinner } from '@Components/Spinner';
import { AlertDialog } from '@Components/AlertDialog';
import { PendingChangesControls } from '@Components/PendingChanges';
import { Intent, Placement, Size, StretchMode } from '@Components/designConstants';
import { useAppSelector } from '../../redux';
import * as UserRedux from '../../redux/user';
import { useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useMap } from '../../utils/hooks/useData';
import { useExperimentFlag } from '../../utils/experiment-context';
import { SegmentationMappingLambdaFetch } from '../../utils/fetch-utils';
import {
  CustomSegmentCard,
  NewSegmentCard,
  SegmentType,
  SegmentTypeValue
} from './CustomSegmentCard';
import { SegmentGroupCard } from './SegmentGroupCard';
import { useGroupedSegments } from './segmentationMappingUtils';
import {
  CommittedValue,
  CustomSegmentsData,
  NewValue,
  PRESET_VALUES_MAP,
  PresetSegmentNames,
  PresetSegmentNameType,
  SystemGeneratedSegmentNames
} from './segmentation-mapping-constants';

type CombinedNewData = Record<
  string,
  {
    name: string;
    values: string[];
  }
>;

const getSegmentTypeFromName = (name: string): SegmentTypeValue => {
  if ((Object.values(SystemGeneratedSegmentNames) as string[]).includes(name)) {
    return SegmentType.SYSTEM_GENERATED;
  }

  if (isBillingSegmentName(name) || name === PresetSegmentNames.BILLING_CATEGORY) {
    return SegmentType.BILLING;
  }

  if ((Object.values(PresetSegmentNames) as string[]).includes(name)) {
    return SegmentType.PRESET;
  }

  return SegmentType.CUSTOM;
};

const PROTECTED_PHRASES_MAP: Record<PresetSegmentNameType, string[]> = {
  [PresetSegmentNames.TACTIC]: ['prospect', 'retarget', 'winback', 'retention', 'brand'],
  [PresetSegmentNames.FUNNEL_TIER]: ['funnel', 'upper', 'mid', 'lower'],
  [PresetSegmentNames.PROMO]: ['evergreen', 'promo'],
  [PresetSegmentNames.BUDGET_TYPE]: ['performance', 'co-op', 'coop', 'dtc'],
  [PresetSegmentNames.GEO]: [],
  [PresetSegmentNames.BILLING_CATEGORY]: []
};

type SegmentId = string;
type ValueId = string;
type SegmentName = string;

const disabledGranularityTooltipCopy =
  'Reducing segmentation granularity is disabled since it can cause disruptions in data to already labeled rows. Please file a ticket for the BlissPoint App to request a change.';

interface CustomSegmentsProps {
  data: CustomSegmentsData[] | undefined;
  setData: Dispatch<SetStateAction<CustomSegmentsData[] | undefined>>;
  dataGranularity: 'ad' | 'ad_group' | 'campaign';
  setDataGranularity: Dispatch<SetStateAction<'ad' | 'ad_group' | 'campaign' | undefined>>;
  onDeleteSegments: () => void;
}

export const CustomSegments = ({
  data,
  setData,
  dataGranularity,
  setDataGranularity,
  onDeleteSegments
}: CustomSegmentsProps) => {
  const setError = useSetError();
  const { cid } = useCompanyInfo();
  const [newSegments, setNewSegments] = useState<Record<SegmentId, SegmentName>>({});
  const [newGroupedSegments, setNewGroupedSegments] = useState<
    Record<string, Record<SegmentId, SegmentName>>
  >({});

  const [newSegmentValuesMap, setNewSegmentValues, setNewSegmentValuesMap] = useMap<
    SegmentId,
    Record<ValueId, NewValue>
  >({});
  const [editedSegmentNamesMap, setEditedSegmentName, setEditedSegmentNameMap] = useMap<
    SegmentId,
    SegmentName | undefined
  >({});
  const [editedSegmentValueMap, setEditedSegmentValue, setEditedSegmentValueMap] = useMap<
    SegmentId,
    Record<ValueId, CommittedValue>
  >({});
  const [segmentsInEditModeMap, setSegmentsInEditModeValue, setSegmentsInEditModeMap] = useMap<
    string,
    boolean
  >({});
  const [segmentsToDelete, setSegmentsToDelete] = useState</* segmentIds */ (number | string)[]>(
    []
  );
  const [segmentValuesToDeleteMap, setSegmentValuesToDelete, setSegmentValuesToDeleteMap] = useMap<
    SegmentId,
    (ValueId | number)[]
  >({});
  const [saving, setSaving] = useState(false);
  const [selectedGranularity, setSelectedGranularity] = useState<
    'ad' | 'ad_group' | 'campaign' | undefined
  >();
  const [inputContainsLockedValue, setInputContainsLockedValue] = useState(false);
  const [showGranularityWarning, setShowGranularityWarning] = useState(false);
  const role = useAppSelector(UserRedux.roleSelector);
  const isAdmin = role === 0;
  const shouldEnableMetricsTableSegmentGroups = useExperimentFlag(
    'enableMetricsTableSegmentGroups'
  ) as boolean;

  const { groupedSegments, fetchGroupedSegments } = useGroupedSegments({
    company: cid,
    shouldEnableMetricsTableSegmentGroups
  });
  const groupedSegmentNames = Object.keys(groupedSegments);

  useEffect(() => {
    if (!isAdmin && selectedGranularity && selectedGranularity !== dataGranularity) {
      setShowGranularityWarning(true);
    }
  }, [dataGranularity, isAdmin, selectedGranularity]);

  const dataGranularityOptions: Record<
    string,
    MenuItem<'ad' | 'ad_group' | 'campaign'>
  > = useMemo(() => {
    const campaignDisabled = !isAdmin && dataGranularity !== 'campaign';
    const adGroupDisabled = !isAdmin && dataGranularity === 'ad';

    return {
      campaign: {
        label: 'Campaign',
        value: 'campaign',
        trailingContent: <div style={{ marginLeft: '4px' }}>(default)</div>,
        disabled: campaignDisabled,
        simpleTooltip: campaignDisabled ? disabledGranularityTooltipCopy : undefined,
        simpleTooltipVerticalShift: '-130px'
      },
      ad_group: {
        label: 'Ad Group',
        value: 'ad_group',
        disabled: adGroupDisabled,
        simpleTooltip: adGroupDisabled ? disabledGranularityTooltipCopy : undefined,
        simpleTooltipVerticalShift: '-130px'
      },
      ad: {
        label: 'Ad',
        value: 'ad'
      }
    };
  }, [dataGranularity, isAdmin]);

  const checkIfContainsProtectedPhrase = useCallback(
    (value: string, segmentName: string) => {
      const checkIfInList = (arr: string[], str: string) =>
        arr.reduce((acc, val) => (str.toLocaleLowerCase().includes(val) ? val : acc), '');

      let invalidSegment = '';
      let protectedValue = '';
      (Object.entries(PROTECTED_PHRASES_MAP) as [PresetSegmentNameType, string[]][]).forEach(
        ([lockedSegName, lockedValues]) => {
          const foundProtectedValue = checkIfInList(lockedValues, value);
          if (value.length > 0 && foundProtectedValue && segmentName !== lockedSegName) {
            invalidSegment = lockedSegName;
            protectedValue = foundProtectedValue;
          }
        }
      );

      if (invalidSegment && !isAdmin) {
        setError({
          message: `Please add the preset ${invalidSegment} segment to label campaigns with "${protectedValue}".`
        });
        setInputContainsLockedValue(true);
      } else {
        setInputContainsLockedValue(false);
      }
    },
    [isAdmin, setError]
  );

  // Adds a new segment to a given group
  const addNewGroupedSegment = useCallback(
    (groupName: string) => {
      const segmentId = uuid.v4();
      setNewGroupedSegments(current => ({
        ...current,
        [groupName]: {
          ...(current[groupName] ?? {}),
          [segmentId]: ''
        }
      }));

      const presetValues: Record<string, NewValue> = {};
      const YesValueId = uuid.v4();
      presetValues[YesValueId] = {
        valueId: YesValueId,
        valueName: 'Yes',
        segmentId
      };
      const NoValueId = uuid.v4();
      presetValues[NoValueId] = {
        valueId: NoValueId,
        valueName: 'No',
        segmentId
      };

      setNewSegmentValues(segmentId, presetValues);
      return { segmentId, segmentName: '' };
    },
    [setNewSegmentValues, setNewGroupedSegments]
  );

  // Adds a new custom segment
  const addNewSegment = useCallback(
    (option: string) => {
      const segmentId = uuid.v4();
      const presetSegmentName =
        option in PresetSegmentNames
          ? PresetSegmentNames[option as keyof typeof PresetSegmentNames]
          : undefined;

      setNewSegments(current => ({
        ...current,
        [segmentId]: presetSegmentName ?? ''
      }));

      const presetValues: Record<string, NewValue> = presetSegmentName
        ? PRESET_VALUES_MAP[presetSegmentName].reduce(
            (acc, valueName) => {
              const valueId = uuid.v4();
              acc[valueId] = {
                valueId,
                valueName,
                segmentId
              };
              return acc;
            },
            {} as Record<string, NewValue>
          )
        : {};
      const naValueId = uuid.v4();
      presetValues[naValueId] = {
        valueId: naValueId,
        valueName: 'N/A',
        segmentId
      };

      setNewSegmentValues(segmentId, presetValues);
    },
    [setNewSegmentValues]
  );

  // Adds a new value to a segment
  const addNewSegmentValue = useCallback(
    (valueName: string, segmentId: number | string) => {
      const valueId = uuid.v4();
      setNewSegmentValuesMap(current => ({
        ...current,
        [segmentId]: {
          ...current[segmentId],
          [valueId]: { valueId, valueName, segmentId }
        }
      }));
    },
    [setNewSegmentValuesMap]
  );

  // Discard all unsaved changes
  const discardChanges = useCallback(() => {
    setNewSegments({});
    setNewGroupedSegments({});
    setNewSegmentValuesMap({});
    setEditedSegmentNameMap({});
    setEditedSegmentValueMap({});
    setSegmentsInEditModeMap({});
    setSegmentsToDelete([]);
    setSegmentValuesToDeleteMap({});
    setSelectedGranularity(undefined);
    fetchGroupedSegments();
  }, [
    setEditedSegmentNameMap,
    setEditedSegmentValueMap,
    setNewSegmentValuesMap,
    setSegmentValuesToDeleteMap,
    setSegmentsInEditModeMap,
    fetchGroupedSegments
  ]);

  const hasSegmentEdits = !(
    Object.keys(newSegments).length === 0 &&
    Object.values(newGroupedSegments).filter(val => Boolean(val)).length === 0 &&
    Object.values(newSegmentValuesMap).filter(val => Boolean(val)).length === 0 &&
    Object.values(editedSegmentNamesMap).filter(val => Boolean(val)).length === 0 &&
    Object.values(editedSegmentValueMap).filter(val => Boolean(val)).length === 0
  );

  const hasGranularityEdits = Boolean(
    selectedGranularity && dataGranularity !== selectedGranularity
  );

  const hasSegmentDeletionEdits = segmentsToDelete.length > 0;

  const hasValueDeletionEdits = Object.keys(segmentValuesToDeleteMap).length > 0;

  const schemaColumnConflicts = useMemo(() => {
    const newNames = Object.values(newSegments);
    const newGroupedNames = Object.values(newGroupedSegments).flatMap(group =>
      Object.values(group)
    );
    const editedNames = Object.values(editedSegmentNamesMap).filter(
      (name): name is string => name !== undefined
    );
    return [...newNames, ...newGroupedNames, ...editedNames].filter(
      name => name && isSchemaNameOrPrettyNameCaseInsensitive(name)
    );
  }, [editedSegmentNamesMap, newGroupedSegments, newSegments]);

  const hasSchemaColumnConflict = schemaColumnConflicts.length > 0;

  useEffect(() => {
    if (hasSchemaColumnConflict) {
      setError({
        message: `Name: '${schemaColumnConflicts.join(
          ', '
        )}' is unavailable for custom segments because it conflicts with an existing schema column name.`
      });
    }
  }, [hasSchemaColumnConflict, schemaColumnConflicts, setError]);

  const hasEdits =
    hasSegmentEdits || hasGranularityEdits || hasSegmentDeletionEdits || hasValueDeletionEdits;

  const saveChanges = useCallback(async () => {
    try {
      setSaving(true);

      if (hasSegmentDeletionEdits) {
        await SegmentationMappingLambdaFetch('/deleteSegments', {
          method: 'DELETE',
          body: {
            segmentIds: segmentsToDelete,
            company: cid
          }
        });
      }

      if (hasValueDeletionEdits) {
        const segmentValuesToDelete = segmentValuesToDeleteMap;
        // Don't try to delete values from a segment that was deleted
        segmentsToDelete.forEach(segmentId => {
          delete segmentValuesToDelete[segmentId];
        });

        const valueIds = Object.values(segmentValuesToDelete).flat();

        if (valueIds.length > 0) {
          await SegmentationMappingLambdaFetch('/deleteSegmentValues', {
            method: 'DELETE',
            body: {
              valueIds,
              company: cid
            }
          });
        }
      }

      if (hasSegmentDeletionEdits || hasValueDeletionEdits) {
        onDeleteSegments();
      }

      if (hasSegmentEdits) {
        const combinedNewData: CombinedNewData = {};

        for (const [segmentId, segmentName] of Object.entries(newSegments)) {
          const valuesMap = newSegmentValuesMap[segmentId] ?? {};
          combinedNewData[segmentId] = {
            name: segmentName,
            values: Object.values(valuesMap)
              .map(val => val.valueName)
              // Do not save empty string values
              .filter(val => Boolean(val))
          };
        }

        const combinedNewGroupedData: Record<string, CombinedNewData> = {};
        for (const groupName of Object.keys(newGroupedSegments)) {
          combinedNewGroupedData[groupName] = {};
          for (const [segmentId, segmentName] of Object.entries(newGroupedSegments[groupName])) {
            const valuesMap = newSegmentValuesMap[segmentId] ?? {};
            combinedNewGroupedData[groupName][segmentId] = {
              name: segmentName,
              values: Object.values(valuesMap)
                .map(val => val.valueName)
                // Do not save empty string values
                .filter(val => Boolean(val))
            };
          }
        }

        const newValuesForExistingSegments: { name: string; segmentId: string }[] = [];
        data?.forEach(({ segmentId }) => {
          const newValues = newSegmentValuesMap[segmentId] ?? {};
          Object.keys(newValues).forEach(key => {
            const valueName =
              (editedSegmentValueMap[segmentId] ?? {})[key]?.valueName ||
              newValues[key]?.valueName ||
              '';
            // Do not save empty string values
            if (valueName) {
              newValuesForExistingSegments.push({ name: valueName, segmentId });
            }
          });
        });

        const newSegmentNameToValuesMap: Record<string, string[]> = {};
        for (const obj of Object.values(combinedNewData)) {
          newSegmentNameToValuesMap[obj.name] = obj.values;
        }

        const newGroupedSegmentNameToValuesMap: Record<string, Record<string, string[]>> = {};
        for (const groupName of Object.keys(combinedNewGroupedData)) {
          newGroupedSegmentNameToValuesMap[groupName] = {};
          for (const segment of Object.values(combinedNewGroupedData[groupName])) {
            newGroupedSegmentNameToValuesMap[groupName][segment.name] = segment.values;
          }
        }

        const editedSegmentNames: Record<string, string> = {};
        Object.keys(editedSegmentNamesMap).forEach(key => {
          const value = editedSegmentNamesMap[key];
          if (value) {
            editedSegmentNames[key] = value;
          }
        });

        const editedSegmentValueNames: Record<string, string> = {};
        Object.keys(editedSegmentValueMap).forEach(segmentId => {
          const values = editedSegmentValueMap[segmentId];
          if (values) {
            Object.keys(values).forEach(valueId => {
              const value = values[valueId];
              const valueIsNew = newValuesForExistingSegments
                .map(val => val.name)
                .includes(value.valueName);
              if (value && !valueIsNew) {
                editedSegmentValueNames[valueId] = value.valueName;
              }
            });
          }
        });

        await SegmentationMappingLambdaFetch('/updateCustomSegments', {
          method: 'POST',
          body: {
            company: cid,
            newSegments: newSegmentNameToValuesMap,
            newValuesForExistingSegments,
            editedSegmentNames,
            editedSegmentValueNames,
            newGroupedSegments: newGroupedSegmentNameToValuesMap
          }
        });
      }

      if (hasGranularityEdits) {
        await SegmentationMappingLambdaFetch('/updateGranularity', {
          method: 'POST',
          body: {
            company: cid,
            granularity: selectedGranularity
          }
        });
      }

      setData(undefined);
      setDataGranularity(selectedGranularity);
      discardChanges();
      setSaving(false);
    } catch (e) {
      setSaving(false);
      setError({ message: `Failed to save changes: ${e.message}`, reportError: e });
    }
  }, [
    hasSegmentDeletionEdits,
    hasValueDeletionEdits,
    hasSegmentEdits,
    hasGranularityEdits,
    setData,
    onDeleteSegments,
    discardChanges,
    segmentsToDelete,
    segmentValuesToDeleteMap,
    data,
    editedSegmentNamesMap,
    editedSegmentValueMap,
    cid,
    newSegments,
    newSegmentValuesMap,
    newGroupedSegments,
    selectedGranularity,
    setDataGranularity,
    setError
  ]);

  const deleteSegmentValue = (segmentId: string, valueId: number | string): void => {
    if (Object.keys(newSegmentValuesMap[segmentId] ?? []).includes(String(valueId))) {
      // If the value is net new, remove it from local state
      setNewSegmentValuesMap(current => {
        const segmentValues = current[segmentId] as Record<string, NewValue>;
        const { [String(valueId)]: _omit, ...remaining } = segmentValues;
        return { ...current, [segmentId]: remaining };
      });
    } else {
      // Else, prep it for a network request
      setSegmentValuesToDelete(segmentId, [
        ...(segmentValuesToDeleteMap[segmentId] ?? []),
        valueId
      ]);
    }
  };

  const deleteNewGroupedSegment = (groupName: string, segmentId: number | string) => {
    setNewGroupedSegments(current => {
      const group = current[groupName] ?? {};
      const { [String(segmentId)]: _omit, ...remaining } = group;
      return { ...current, [groupName]: remaining };
    });
  };

  const unaddedPresets = useMemo(() => {
    if (!data) {
      return Object.keys(PresetSegmentNames);
    }

    return Object.keys(PresetSegmentNames).filter(
      presetKey =>
        !data
          .map(segment => segment.segmentName)
          .includes(PresetSegmentNames[presetKey as keyof typeof PresetSegmentNames]) &&
        !Object.values(newSegments).includes(
          PresetSegmentNames[presetKey as keyof typeof PresetSegmentNames]
        )
    );
  }, [data, newSegments]);

  const filteredNewSegments: Record<string, string> = useMemo(() => {
    const filteredSegments = { ...newSegments };
    segmentsToDelete.forEach(segmentId => {
      if (filteredSegments[segmentId]) {
        delete filteredSegments[segmentId];
      }
    });
    return filteredSegments;
  }, [newSegments, segmentsToDelete]);

  const filteredCommittedSegments: CustomSegmentsData[] = useMemo(
    () =>
      data?.filter(
        segment =>
          !segmentsToDelete.includes(segment.segmentId) &&
          !Object.values(groupedSegments)
            .flatMap(gs => gs.map(g => g.segmentId))
            .some(id => id === Number(segment.segmentId))
      ) ?? [],
    [data, segmentsToDelete, groupedSegments]
  );

  const filteredGroupedSegments = useCallback(
    (groupName: string) =>
      groupedSegments[groupName].filter(segment => !segmentsToDelete.includes(segment.segmentId)),
    [groupedSegments, segmentsToDelete]
  );

  const groupHasEdits = useCallback(
    (groupName: string): boolean => {
      const groupSegmentIds = (groupedSegments[groupName] ?? []).map(segment => segment.segmentId);
      const hasNewSegments = Object.keys(newGroupedSegments[groupName] ?? {}).length > 0;
      const hasRenamedSegments = groupSegmentIds.some(id => Boolean(editedSegmentNamesMap[id]));
      const hasDeletedSegments = groupSegmentIds.some(id => segmentsToDelete.includes(id));
      return hasNewSegments || hasRenamedSegments || hasDeletedSegments;
    },
    [groupedSegments, newGroupedSegments, editedSegmentNamesMap, segmentsToDelete]
  );

  if (!data) {
    return <FullPageSpinner />;
  }

  return (
    <>
      <AlertDialog
        intent={Intent.WARNING}
        show={showGranularityWarning}
        onHide={() => setShowGranularityWarning(false)}
        icon={undefined}
        title={`Are you sure you want to set the granularity level to ${
          dataGranularityOptions[selectedGranularity ?? dataGranularity].label
        }?`}
        body='You will not be able to undo this. Only change granularity if you want to permanently segment your data at another level. Make sure you have consulted with the rest of your team before changing this option.'
        primaryAction={{ label: 'Continue' }}
        secondaryAction={{
          onClick: () => {
            setSelectedGranularity(undefined);
            setShowGranularityWarning(false);
          }
        }}
      />
      <div className='custom-segments-page'>
        <div className='custom-segments-header'>
          <div className='custom-segments-title'>Our Segments</div>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            stretch={StretchMode.FIT}
            onChange={(optionId: 'ad' | 'ad_group' | 'campaign') => {
              setSelectedGranularity(optionId);
            }}
            options={Object.values(dataGranularityOptions)}
            value={dataGranularityOptions[selectedGranularity ?? dataGranularity].value}
            label='Granularity'
          />
        </div>
        <div className='segment-list'>
          {filteredCommittedSegments.map(segmentObj => {
            const { segmentId, segmentName, values } = segmentObj;
            const editedValues = editedSegmentValueMap[segmentId] ?? {};
            const newAndExistingValues = [
              ...values,
              ...Object.values(newSegmentValuesMap[segmentId] ?? {})
            ];
            const valuesWithEdits = newAndExistingValues.map(value => {
              if (editedValues[value.valueId]) {
                return {
                  ...value,
                  valueName: editedValues[value.valueId].valueName
                };
              }
              return value;
            });
            const filteredValues = valuesWithEdits.filter(
              ({ valueId }) => !(segmentValuesToDeleteMap[segmentId] ?? []).includes(valueId)
            );

            const cardHasEdits =
              Object.keys(newSegmentValuesMap[segmentId] ?? {}).length > 0 ||
              Object.keys(editedSegmentValueMap[segmentId] ?? {}).length > 0 ||
              Boolean(editedSegmentNamesMap[segmentId]);

            return (
              <CustomSegmentCard
                type={getSegmentTypeFromName(segmentName)}
                key={segmentName}
                id={segmentId}
                name={editedSegmentNamesMap[segmentId] ?? segmentName}
                values={filteredValues}
                dataGranularity={dataGranularity}
                isInEditMode={Boolean(segmentsInEditModeMap[segmentId])}
                setIsInEditMode={newVal => setSegmentsInEditModeValue(segmentId.toString(), newVal)}
                isAdmin={isAdmin}
                hasEdits={cardHasEdits}
                onEditSegmentName={newVal => {
                  checkIfContainsProtectedPhrase(newVal, segmentName);
                  setEditedSegmentName(segmentId.toString(), newVal);
                }}
                onEditSegmentValueName={(newVal, valueId) => {
                  checkIfContainsProtectedPhrase(newVal, segmentName);
                  setEditedSegmentValue(segmentId, {
                    ...editedSegmentValueMap[segmentId],
                    [valueId]: {
                      ...(editedSegmentValueMap[segmentId] ?? {})[valueId],
                      valueName: newVal,
                      valueId: Number(valueId)
                    }
                  });
                }}
                addNewSegmentValue={addNewSegmentValue}
                deleteSegment={() => {
                  setSegmentsToDelete(current => [...current, segmentId]);
                }}
                deleteSegmentValue={deleteSegmentValue}
              />
            );
          })}
          {groupedSegmentNames.map(groupName => (
            <SegmentGroupCard
              key={groupName}
              groupName={groupName}
              segments={filteredGroupedSegments(groupName)}
              hasEdits={groupHasEdits(groupName)}
              onEditSegmentName={(newVal, segmentId) => {
                if (Object.hasOwn(newGroupedSegments[groupName] ?? {}, segmentId.toString())) {
                  setNewGroupedSegments(current => ({
                    ...current,
                    [groupName]: {
                      ...current[groupName],
                      [segmentId]: newVal
                    }
                  }));
                } else {
                  setEditedSegmentName(segmentId.toString(), newVal);
                }
              }}
              addNewGroupedSegment={addNewGroupedSegment}
              newGroupedSegments={newGroupedSegments[groupName]}
              editedSegmentNamesMap={editedSegmentNamesMap}
              deleteNewGroupedSegment={deleteNewGroupedSegment}
              deleteGroupedSegment={segmentId => {
                setSegmentsToDelete(current => [...current, segmentId]);
              }}
              isInEditMode={Boolean(segmentsInEditModeMap[groupName])}
              setIsInEditMode={newVal => setSegmentsInEditModeValue(groupName, newVal)}
            />
          ))}
          {Object.keys(filteredNewSegments).map(segmentId => {
            const segmentName = newSegments[segmentId] ?? '';
            const values = Object.values(newSegmentValuesMap[segmentId] ?? {});

            const isPresetName = (Object.values(PresetSegmentNames) as string[]).includes(
              segmentName
            );
            let cardType: SegmentTypeValue;
            if (segmentName === PresetSegmentNames.BILLING_CATEGORY) {
              cardType = SegmentType.BILLING;
            } else if (isPresetName) {
              cardType = SegmentType.PRESET;
            } else {
              cardType = SegmentType.CUSTOM;
            }

            return (
              <CustomSegmentCard
                type={cardType}
                key={segmentId}
                id={segmentId}
                name={segmentName}
                values={values}
                dataGranularity={dataGranularity}
                isInEditMode
                isAdmin={isAdmin}
                hasEdits
                onEditSegmentName={newVal => {
                  setNewSegments(current => ({
                    ...current,
                    [segmentId]: newVal
                  }));
                }}
                onEditSegmentValueName={(newVal, valueId) => {
                  checkIfContainsProtectedPhrase(newVal, segmentName);
                  setNewSegmentValuesMap(current => ({
                    ...current,
                    [segmentId]: {
                      ...current[segmentId],
                      [valueId]: { valueId, segmentId, valueName: newVal }
                    }
                  }));
                }}
                addNewSegmentValue={addNewSegmentValue}
                deleteSegment={() => {
                  setNewSegments(current => {
                    const { [segmentId]: _omit, ...rest } = current;
                    return rest;
                  });
                  setNewSegmentValuesMap(current => {
                    const { [segmentId]: _omit, ...rest } = current;
                    return rest;
                  });
                }}
                deleteSegmentValue={deleteSegmentValue}
              />
            );
          })}
          <NewSegmentCard unaddedPresets={unaddedPresets} addNewSegment={addNewSegment} />
        </div>
        <div className='custom-segments-footer'>
          <PendingChangesControls
            hasPendingChanges={hasEdits}
            saveChanges={saveChanges}
            isSaving={saving}
            clearAllChanges={discardChanges}
            disableSaveButton={
              Object.values(newSegments).some(value => value === '') ||
              Object.values(newGroupedSegments)
                .flatMap(group => Object.values(group))
                .some(value => !value || value.trim() === '') ||
              inputContainsLockedValue ||
              hasSchemaColumnConflict
            }
          />
        </div>
      </div>
    </>
  );
};
